import React, { FC, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useStore } from '../../store/store-provider';
import Header from '../../components/header/header';
import Chat from '../../components/chat/chat';
import { PresenterSlideNotification } from '../../components';
import LaunchPreviewModal from '../../components/launch-preview-modal/launch-preview-modal';
import LiveStreamInfoComponent from '../../components/shared/live-stream-info/live-stream-info';
import PresenterFullsrceenSlide from '../../components/presenter/presenter-fullscreen-slide';

interface IProps {
  urlPath: string;
}

export interface IRouteParams {
  id: string;
}

const ContainerPage: FC<IProps> = observer(({ urlPath, children }): JSX.Element => {
  const {
    podiumInfoStore,
    chatStore,
    permissionsStore,
    attendeesStore,
    pollsStore,
    liveStreamStatusStore,
    slideManagementStore
  } = useStore();

  const { isFullScreenModeSafari } = pollsStore;
  const {
    getLiveInfoForStatus,
    getLivePresentationId,
    liveStatus,
    onKnovioPublishCommand,
    onKnovioCommand
  } = liveStreamStatusStore;
  const { getIsSlidesFullscreenOpen } = slideManagementStore;
  const { id: presenterEmbedId } = useParams<IRouteParams>();
  const { setHashId } = chatStore;
  const {
    presentationInfo: { presentationName, interfaceBrandingLogoUrl },
    getPresentationInfo,
    getPresenterReviewMode,
    onUpdatePresenterReviewModeSubscription,
    getPresentationId,
    setWindowResolutionInfo,
    getPreviewInformation,
    isShowLaunchPreviewModal
  } = podiumInfoStore;

  const { getActivityAttendeeCountService } = attendeesStore;
  const { getAttendeesCountInterval, clearAttendeesCountInterval } = getActivityAttendeeCountService();

  const {
    getPermissions,
    isPodiumSlidesEnabled,
    isPodiumMessagesEnabled,
    isPodiumPollsEnabled,
    isPodiumAttendeesEnabled,
    isPodiumEnabled,
    isPodiumQandaEnabled,
    isPodiumPresenterQuestionsEnabled,

  } = permissionsStore;

  const isPollsEnabled = isPodiumPollsEnabled();
  const isSlidesEnabled = isPodiumSlidesEnabled();
  const isChatEnabled = isPodiumMessagesEnabled();
  const isAttendeesEnabled = isPodiumAttendeesEnabled();
  const isQandaEnabled = isPodiumQandaEnabled();
  const isPodiumPermissionEnabled = isPodiumEnabled();
  const isPresenterQuestionsEnabled = isPodiumPresenterQuestionsEnabled();
  const setWindowResolutionCallback = useCallback(() => setWindowResolutionInfo(), []);

  useEffect(() => {
    setHashId(presenterEmbedId);
    getPermissions()
      .then(() => isPodiumAttendeesEnabled() && getAttendeesCountInterval());
    getLiveInfoForStatus();
    getPresenterReviewMode();
    getPresentationInfo(presenterEmbedId);
    window.addEventListener('resize', setWindowResolutionCallback);
    return () => {
      window.removeEventListener('resize', setWindowResolutionCallback);
      clearAttendeesCountInterval();
    };
  }, []);

  useEffect(() => {
    if (!getPresentationId()) return;
    const onUpdateReviewModeSubscription = onUpdatePresenterReviewModeSubscription();
    return () => {
      onUpdateReviewModeSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!getLivePresentationId()) return;
    onKnovioPublishCommand();
    onKnovioCommand();
    return () => {
      onKnovioPublishCommand().unsubscribe();
      onKnovioCommand().unsubscribe();
    };
  }, [getLivePresentationId()]);

  return (
    <div className="kv-presenter">
      {isSlidesEnabled && getIsSlidesFullscreenOpen && <PresenterFullsrceenSlide/>}
      {isChatEnabled && <Chat/>}
      {isSlidesEnabled && <PresenterSlideNotification/>}
      {isShowLaunchPreviewModal && <LaunchPreviewModal/>}
      {!isFullScreenModeSafari && (
        <Header
          isPodiumEnabled={isPodiumPermissionEnabled}
          isQandaEnabled={isQandaEnabled}
          isPodiumPollsEnabled={isPollsEnabled}
          isPodiumAttendeesEnabled={isAttendeesEnabled}
          isPodiumSlidesEnabled={isSlidesEnabled}
          presentationName={presentationName}
          interfaceBrandingLogoUrl={interfaceBrandingLogoUrl}
          isPodiumPresenterQuestionsEnabled={isPresenterQuestionsEnabled}
          urlPath={urlPath}
          getPreviewInformation={getPreviewInformation}
          hasNavigation
        />
      )}
      {getLivePresentationId() && <LiveStreamInfoComponent status={liveStatus}/>}
      <main>
        {children}
      </main>
    </div>
  );
});

export default ContainerPage;
